/**
 * Client-side downscaling of a picked image. Two reasons it belongs in the
 * browser, both about correctness:
 *
 * - Re-encoding through a canvas APPLIES the EXIF orientation and STRIPS the
 *   EXIF block — GPS position and device ids included. A server that ignores
 *   EXIF would store phone portraits sideways.
 * - Shrinking later is trivial, recovering detail is not — so keep the master
 *   generous (1200 px by default) and crop for display with CSS.
 *
 * A convenience, not a security measure: the server still has to check type
 * and size on its own. Animated GIFs come out as their first frame.
 */

export interface NxDownscaleOptions {
  /** Longest edge in pixels. Defaults to 1200. Smaller images are never enlarged. */
  maxEdge?: number;
  /** Output type. Defaults to `image/jpeg`; use `image/webp` or `image/png` to keep transparency. */
  type?: 'image/jpeg' | 'image/webp' | 'image/png';
  /** Encoder quality for JPEG / WebP, 0–1. Defaults to 0.85. */
  quality?: number;
  /** Fill behind transparent pixels for JPEG (which has no alpha). Defaults to white. */
  background?: string;
}

export const NX_DOWNSCALE_DEFAULTS = {
  maxEdge: 1200,
  type: 'image/jpeg',
  quality: 0.85,
  background: '#ffffff',
} as const satisfies Required<NxDownscaleOptions>;

/**
 * The size that fits `maxEdge` with the aspect ratio kept. The long edge lands
 * on `maxEdge` exactly (no 1199 from rounding); an image already within the
 * bound keeps its size; a zero or negative dimension yields `0 × 0`.
 */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number = NX_DOWNSCALE_DEFAULTS.maxEdge
): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: 0, height: 0 };
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width: Math.round(width), height: Math.round(height) };
  const factor = maxEdge / longest;
  return width >= height
    ? { width: maxEdge, height: Math.max(1, Math.round(height * factor)) }
    : { width: Math.max(1, Math.round(width * factor)), height: maxEdge };
}

interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

async function decode(file: Blob): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    // Spelled out: applying the orientation is the point, and the default
    // has differed between engines.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      // A 12-megapixel photo is ~48 MB of RGBA; free it right away.
      release: () => bitmap.close(),
    };
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('downscaleImage: not a decodable image'));
      el.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

const EXTENSION: Record<NonNullable<NxDownscaleOptions['type']>, string> = {
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/png': 'png',
};

/**
 * Decodes an image, scales it to fit `maxEdge` and re-encodes it (EXIF
 * applied and stripped). A `File` comes back as a `File` with the extension of
 * the new type. Rejects when the file is not a decodable image.
 */
export async function downscaleImage(file: File, options?: NxDownscaleOptions): Promise<File>;
export async function downscaleImage(file: Blob, options?: NxDownscaleOptions): Promise<Blob>;
export async function downscaleImage(file: Blob, options: NxDownscaleOptions = {}): Promise<Blob> {
  const opts = { ...NX_DOWNSCALE_DEFAULTS, ...options };
  const decoded = await decode(file);
  let blob: Blob;
  try {
    const target = fitWithin(decoded.width, decoded.height, opts.maxEdge);
    if (target.width === 0) throw new Error('downscaleImage: image has no usable size');
    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('downscaleImage: no 2d canvas context');
    if (opts.type === 'image/jpeg') {
      ctx.fillStyle = opts.background;
      ctx.fillRect(0, 0, target.width, target.height);
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(decoded.source, 0, 0, target.width, target.height);
    blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (out) =>
          out ? resolve(out) : reject(new Error('downscaleImage: canvas produced no blob')),
        opts.type,
        opts.quality
      );
    });
  } finally {
    decoded.release();
  }
  if (typeof File !== 'undefined' && file instanceof File) {
    const base = file.name.replace(/\.[^./\\]+$/, '') || 'bild';
    return new File([blob], `${base}.${EXTENSION[opts.type]}`, { type: opts.type });
  }
  return blob;
}
